import React, { useRef } from 'react';
import { FieldWrapper } from '@progress/kendo-react-form';
import { FloatingLabel, Error, Hint } from '@progress/kendo-react-labels';
import BaseSelect from '../inputs/BaseSelect';
// eslint-disable-next-line no-unused-vars
import BaseFormControl from './BaseFormControl';

/**
 * A select form field.
 *
 * @typedef {React.ComponentProps<BaseFormControl>} BaseFieldRenderProps
 * @typedef {React.ComponentPropsWithoutRef<BaseSelect>} BaseSelectProps
 *
 * @param {BaseFieldRenderProps & BaseSelectProps} fieldRenderProps
 * @returns {React.ReactElement} Select form field.
 */
const FormSelect = (fieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    name,
    valid,
    disabled,
    hint,
    wrapperStyle,
    clearButton,
    wrapperClassName,
    optional,
    value,
    ...others
  } = fieldRenderProps;

  const editorId = id ?? name;

  const editorRef = useRef(null);
  const showValidationMessage = touched && validationMessage;
  const showHint = !showValidationMessage && hint;
  const hintId = showHint ? `${editorId}_hint` : '';
  const errorId = showValidationMessage ? `${editorId}_error` : '';
  const labelId = label ? `${editorId}_label` : '';

  if (!label) {
    return (
      <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
        <BaseSelect
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          clearButton={disabled ? false : clearButton}
          value={value}
          dataTestId={editorId}
          {...others}
        />
        {showHint && <Hint id={hintId}>{hint}</Hint>}
        {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
      </FieldWrapper>
    );
  }

  return (
    <FieldWrapper style={wrapperStyle} className={wrapperClassName}>
      <FloatingLabel
        label={label}
        id={labelId}
        optional={optional}
        editorRef={editorRef}
        editorId={editorId}
        editorValid={valid}
        editorDisabled={disabled}
        editorValue={(value === false || value === 0) ? `${value}` : value ?? null}
      >
        <BaseSelect
          ariaLabelledBy={labelId}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ref={editorRef}
          valid={valid}
          id={editorId}
          name={name}
          disabled={disabled}
          clearButton={disabled ? false : clearButton}
          value={value}
          dataTestId={editorId}
          {...others}
        />
      </FloatingLabel>
      {showHint && <Hint id={hintId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export default FormSelect;
